import { useState, useEffect } from 'react';
import { Phone, Shield, PlayCircle } from 'lucide-react';

import { expectationSteps } from '../data/mock';
import { substituteVariables } from '../utils/templateVariables';
import { apiService, isPrerendering } from '../services/api';

import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
// import ContactModal from './ContactModal'; // TODO: re-enable if contact form returns

const ExpectationSection = () => {
  const [steps, setSteps] = useState(expectationSteps);
  const [cta, setCta] = useState({
    button_text: 'Get Started Today — QuickStart {{quickstart_fee}}',
    section_title: 'What to Expect',
    section_subtitle: 'Starting your martial arts journey is simple. Here\'s how we\'ll guide you through every step of the process.'
  });
  const [businessInfo, setBusinessInfo] = useState(null);
  const iconMap = {
    1: Phone,
    2: Shield,
    3: PlayCircle
  };

  useEffect(() => {
    // Skip API calls during pre-rendering (react-snap)
    if (isPrerendering()) return;

    const fetchExpectations = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/expectations`);
        if (response.ok) {
          const data = await response.json();
          if (data.steps && data.steps.length > 0) {
            setSteps(data.steps);
          }
          if (data.cta) {
            setCta(data.cta);
          }
        }
      } catch (error) {
        console.error('Failed to fetch expectations');
      }
    };

    const fetchBusinessInfo = async () => {
      const info = await apiService.getBusinessInfo();
      if (info) setBusinessInfo(info);
    };

    fetchExpectations();
    fetchBusinessInfo();
  }, []);

  const handleGetStarted = () => {
    window.location.href = 'tel:+13524280086';
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-100 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {cta.section_title || 'What to Expect'}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {cta.section_subtitle || 'Starting your martial arts journey is simple. Here\'s how we\'ll guide you through every step of the process.'}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {steps.map((step) => {
            const IconComponent = iconMap[step.step] || Phone;

            return (
              <Card key={step.id || step.step} className="mb-8 last:mb-0 shadow-lg border-0 overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row">
                    {/* Step Number & Icon */}
                    <div className="bg-red-600 text-white p-8 md:w-64 flex flex-col items-center justify-center text-center">
                      <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
                        <IconComponent className="h-8 w-8" />
                      </div>
                      <div className="text-3xl font-bold mb-2">Step {step.step}</div>
                    </div>

                    {/* Content */}
                    <div className="p-8 flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {substituteVariables(step.title, businessInfo)}
                      </h3>
                      <p className="text-gray-600 leading-relaxed text-lg">
                        {substituteVariables(step.description, businessInfo)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Background Check Notice */}
        <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-4xl mx-auto">
          <div className="text-center">
            <Shield className="h-8 w-8 text-blue-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Enhanced Safety Measures</h3>
            <p className="text-blue-800">
              All adult memberships require a completed background check with your consent.
              This ensures a safe, professional environment for all our students and families.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12 px-4">
          <Button
            size="lg"
            className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white font-bold px-6 sm:px-10 py-4 text-base sm:text-lg shadow-xl"
            onClick={handleGetStarted}
          >
            Book Your Appointment
          </Button>
        </div>
      </div>

      {/* ContactModal removed — all CTAs now call Jim Rivera's direct line tel:+13524280086 */}
      {/* TODO: re-enable <ContactModal> here if contact form returns */}
    </section>
  );
};

export default ExpectationSection;
